(function () {
  'use strict';

  /**
   * <hero-slideshow>
   *
   * Two independent image panels (left / right) that each advance on their own
   * random timer. Once BOTH panels have advanced since the last product change,
   * the centred product image (and its matching product link) advances too.
   */
  class HeroSlideshow extends HTMLElement {
    constructor() {
      super();

      this.timers = { left: null, right: null, product: null, productDelay: null };
      this.index = { left: 0, right: 0, product: 0 };
      this.changedSinceProduct = { left: false, right: false };
      this.isRunning = false;
      this.isVisible = true;
      this.isSelectedInEditor = false;

      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.minInterval = parseInt(this.dataset.intervalMin, 10) || 8000;
      this.maxInterval = Math.max(this.minInterval, parseInt(this.dataset.intervalMax, 10) || 10000);
      this.productDelay = parseInt(this.dataset.productDelay, 10) || 0;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.slides = {
        left: Array.from(this.querySelectorAll('[data-panel="left"] [data-slide]')),
        right: Array.from(this.querySelectorAll('[data-panel="right"] [data-slide]')),
        product: Array.from(this.querySelectorAll('[data-panel="product"] [data-slide]')),
      };
      this.productLinks = Array.from(this.querySelectorAll('[data-product-link]'));

      // A panel with a single image can never "change", so treat it as always satisfied.
      this.changedSinceProduct.left = this.slides.left.length < 2;
      this.changedSinceProduct.right = this.slides.right.length < 2;

      this.setActive('left', 0);
      this.setActive('right', 0);
      this.setActive('product', 0);

      this.observeVisibility();
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    /* ---------- Scheduling ---------- */

    randomInterval() {
      return Math.floor(this.minInterval + Math.random() * (this.maxInterval - this.minInterval));
    }

    start() {
      if (this.isRunning || this.reduceMotion || !this.isVisible || this.isSelectedInEditor) return;
      this.isRunning = true;

      if (this.slides.left.length > 1) this.schedule('left');
      if (this.slides.right.length > 1) this.schedule('right');

      // If neither side panel rotates, let the product rotate on its own timer.
      if (this.slides.left.length < 2 && this.slides.right.length < 2 && this.slides.product.length > 1) {
        this.schedule('product');
      }
    }

    stop() {
      this.isRunning = false;
      Object.keys(this.timers).forEach((key) => {
        clearTimeout(this.timers[key]);
        this.timers[key] = null;
      });
    }

    schedule(panel) {
      clearTimeout(this.timers[panel]);
      this.timers[panel] = setTimeout(() => {
        if (!this.isRunning) return;
        this.advance(panel);
        this.schedule(panel);
      }, this.randomInterval());
    }

    /* ---------- Slide changes ---------- */

    advance(panel) {
      const slides = this.slides[panel];
      if (slides.length < 2) return;

      const next = (this.index[panel] + 1) % slides.length;
      this.setActive(panel, next);

      if (panel === 'left' || panel === 'right') {
        this.changedSinceProduct[panel] = true;
        this.maybeAdvanceProduct();
      }
    }

    maybeAdvanceProduct() {
      if (!this.changedSinceProduct.left || !this.changedSinceProduct.right) return;

      this.changedSinceProduct.left = this.slides.left.length < 2;
      this.changedSinceProduct.right = this.slides.right.length < 2;

      if (this.slides.product.length < 2) return;

      // Wait a beat after the second panel changes so the product swap reads as its own moment
      clearTimeout(this.timers.productDelay);
      this.timers.productDelay = setTimeout(() => {
        if (!this.isRunning) return;
        this.setActive('product', (this.index.product + 1) % this.slides.product.length);
      }, this.productDelay);
    }

    setActive(panel, index) {
      const slides = this.slides[panel];
      if (!slides.length) return;

      this.index[panel] = index;

      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      if (panel === 'product') {
        this.productLinks.forEach((link, i) => {
          const active = i === index;
          link.classList.toggle('is-active', active);
          link.setAttribute('aria-hidden', active ? 'false' : 'true');
          link.tabIndex = active ? 0 : -1;
        });
      }
    }

    /* ---------- Pausing ---------- */

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isVisible = entry.isIntersecting;
            if (this.isVisible) {
              this.start();
            } else {
              this.stop();
            }
          });
        },
        { threshold: 0.1 }
      );

      this.observer.observe(this);
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stop();
      } else {
        this.start();
      }
    }

    /* ---------- Theme editor ---------- */

    onBlockSelect(event) {
      const block = event.target.closest('[data-slide]');
      if (!block || !this.contains(block)) return;

      const panel = block.closest('[data-panel]').dataset.panel;
      const index = this.slides[panel].indexOf(block);
      if (index === -1) return;

      this.isSelectedInEditor = true;
      this.stop();
      this.setActive(panel, index);
    }

    onBlockDeselect(event) {
      const block = event.target.closest('[data-slide]');
      if (!block || !this.contains(block)) return;

      this.isSelectedInEditor = false;
      this.start();
    }
  }

  if (!customElements.get('hero-slideshow')) {
    customElements.define('hero-slideshow', HeroSlideshow);
  }
})();
